/**
 * MarketingTourism · v2.6.9 batch CF (reworked from BT)
 * ======================================================
 * Public /tourism INDEX page. Renamed from "long-form city blocks" to
 * "compact city grid with LEARN MORE buttons". The heavy detail content
 * (paragraphs + attractions) now lives on the per-city detail page at
 * /tourism/:slug — see MarketingTourismCity.jsx.
 *
 * Layout:
 *   • Full-width header image (admin-uploadable via CMS) with title overlay
 *   • Intro paragraph
 *   • Responsive 1/2/3-col grid of city blocks · each block has:
 *       - City image (with "LEARN MORE!" button overlay at the bottom)
 *       - Short intro teaser paragraph
 *       - "Book a ride" + "Call dispatch" CTAs
 *
 * Inactive cities (enabled === false) are filtered out so admins can
 * toggle a city off without deleting it.
 */
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, ArrowRight, Phone, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

// v2.6.9 batch CH · Pagination — max 3 cities per row × 4 rows = 12 per page.
const PAGE_SIZE = 12;

export default function MarketingTourism() {
  const { cfg, loading } = useSiteConfig();
  // v2.6.9 batch CH · Pagination — page index 1-based.
  const [page, setPage] = useState(1);
  if (loading || !cfg) {
    return (
      <MarketingLayout>
        <div className="py-24 text-center">
          <Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500" data-testid="tourism-loading"/>
        </div>
      </MarketingLayout>
    );
  }
  const t = cfg.tourism || { title: "Local Tourism", intro: "", header_image_url: "", cities: [] };
  const g = cfg.global || {};
  const cities = (t.cities || []).filter((c) => c.enabled !== false);
  const totalPages = Math.max(1, Math.ceil(cities.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const sliced = cities.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hasHeader = !!t.header_image_url;

  return (
    <MarketingLayout>
      {/* v2.6.9 batch CF · Full-width header image — admin uploads via
          Admin → Front End → Tourism → "Header Image". Falls back to a
          dark amber-accented hero when no image is set. */}
      <section
        className="relative border-b-[1px] border-amber-400 overflow-hidden"
        style={
          hasHeader
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.72)), url("${t.header_image_url}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : { backgroundColor: "#0a0a0a" }
        }
        data-testid="tourism-hero"
      >
        <div className="max-w-5xl mx-auto px-5 py-16 sm:py-24 text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">TOURISM · PARTNER CITIES</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-zinc-100 mt-2" data-testid="tourism-title">{t.title}</h1>
          {t.intro && <p className="text-zinc-300 mt-4 max-w-2xl mx-auto leading-relaxed text-base" data-testid="tourism-intro">{t.intro}</p>}
        </div>
      </section>

      {/* City grid */}
      <section className="max-w-7xl mx-auto px-5 py-12" data-testid="tourism-cities">
        {cities.length === 0 ? (
          <div className="text-center py-16 text-zinc-500" data-testid="tourism-empty">
            <MapPin className="h-10 w-10 mx-auto mb-3 opacity-50"/>
            <p>Partner-city tourism guides coming soon.</p>
          </div>
        ) : (
          <>
            {/* v2.6.9 batch CH · Strict 3-up grid at lg+ so we never overflow
                the 4-row × 12-per-page budget. */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tourism-cities-grid">
              {sliced.map((c, idx) => (
                <CityBlock key={c.slug || idx} city={c} idx={(currentPage - 1) * PAGE_SIZE + idx} phone={g.phone}/>
              ))}
            </div>
            {/* Pagination — only renders when there's more than one page. */}
            {totalPages > 1 && (
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onChange={setPage}
                total={cities.length}
              />
            )}
          </>
        )}
      </section>

      {/* Bottom CTA strip · v2.6.9 batch CH · gated by global CMS toggle */}
      {cfg?.global?.show_ready_to_ride_cta && (
      <section className="bg-amber-400 text-zinc-950 py-8" data-testid="tourism-cta-strip">
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between gap-5 flex-wrap">
          <h2 className="text-xl sm:text-2xl font-black uppercase">Plan your tourism trip with Buller Taxi.</h2>
          <Link to="/ride/login">
            <Button className="bg-zinc-950 text-amber-300 hover:bg-zinc-900 font-extrabold uppercase tracking-wider">
              BOOK A TOURISM RIDE <ArrowRight className="h-4 w-4 ml-1"/>
            </Button>
          </Link>
        </div>
      </section>
      )}
    </MarketingLayout>
  );
}

function CityBlock({ city, idx, phone }) {
  const teaser = (city.intro_short && city.intro_short.trim()) || (city.intro || "").split("\n")[0] || "";
  const href = `/tourism/${city.slug || idx}`;
  return (
    <Card className="bg-zinc-900 border-zinc-800 hover:border-amber-400/60 transition-colors overflow-hidden p-0" data-testid={`tourism-city-${city.slug || idx}`}>
      {/* Image + LEARN MORE overlay */}
      <Link to={href} className="block relative group" data-testid={`tourism-city-${city.slug || idx}-image-link`}>
        <div className="aspect-[16/9] bg-zinc-950 overflow-hidden">
          {city.hero_image_url ? (
            <img
              src={city.hero_image_url}
              alt={city.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              data-testid={`tourism-city-${city.slug || idx}-image`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-700">
              <MapPin className="h-10 w-10"/>
            </div>
          )}
        </div>
        {/* Bottom gradient + LEARN MORE button overlay */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none"/>
        <div className="absolute inset-x-0 bottom-0 p-3">
          <button
            type="button"
            className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-[0.18em] text-sm py-2.5 rounded-md flex items-center justify-center gap-1.5 transition-all duration-200 hover:scale-[1.02] shadow-lg shadow-amber-500/40"
            data-testid={`tourism-city-${city.slug || idx}-learn-more`}
          >
            <BookOpen className="h-4 w-4"/> LEARN MORE!
          </button>
        </div>
      </Link>

      {/* Text block */}
      <div className="p-5 space-y-2.5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold flex items-center gap-1.5">
            <MapPin className="h-3 w-3"/>{city.tagline || "PARTNER CITY"}
          </div>
          <h2 className="text-xl font-black text-zinc-100 mt-1 uppercase tracking-wide" data-testid={`tourism-city-${city.slug || idx}-name`}>{city.name}</h2>
        </div>
        {teaser && (
          <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3" data-testid={`tourism-city-${city.slug || idx}-teaser`}>{teaser}</p>
        )}
        <div className="flex flex-wrap gap-2 pt-2">
          <Link to={`/ride/login?destination=${encodeURIComponent(city.name)}`}>
            <Button
              size="sm"
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider"
              data-testid={`tourism-city-${city.slug || idx}-book`}
            >
              BOOK A RIDE <ArrowRight className="h-3 w-3 ml-1"/>
            </Button>
          </Link>
          {phone && (
            <a href={`tel:${phone.replace(/\D/g, "")}`}>
              <Button
                size="sm"
                variant="outline"
                className="bg-transparent border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-zinc-950 font-extrabold uppercase tracking-wider"
                data-testid={`tourism-city-${city.slug || idx}-call`}
              >
                <Phone className="h-3 w-3 mr-1"/>CALL DISPATCH
              </Button>
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * v2.6.9 batch CH · Pagination component for the Tourism index. Pure-CSS
 * numeric page buttons + prev/next chevrons. Generates a compact page list
 * (e.g. 1 2 3 4 5 → ▶ Next) for any pageCount. For larger lists the helper
 * elides the middle range with ellipses so the UI never wraps awkwardly.
 *
 * Props:
 *   page         current 1-based page number
 *   totalPages   total page count
 *   onChange     callback(newPage)
 *   total        total items (for the "Showing X–Y of Z" hint)
 */
function Pagination({ page, totalPages, onChange, total }) {
  // Build the visible page-number set. Always show first, last, current,
  // and one neighbour on each side. Insert "…" markers where we elide.
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, page, page - 1, page + 1]);
    const filtered = [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    filtered.forEach((p, i) => {
      if (i > 0 && p - filtered[i - 1] > 1) out.push("…");
      out.push(p);
    });
    return out;
  }, [page, totalPages]);

  const startIdx = (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="mt-8 flex flex-col items-center gap-2" data-testid="tourism-pagination">
      <div className="flex items-center gap-1 flex-wrap justify-center">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-3 h-9 rounded-md border border-zinc-700 text-zinc-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1 hover:border-amber-400 hover:text-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="tourism-pagination-prev"
        >
          <ChevronLeft className="h-3.5 w-3.5"/>PREV
        </button>
        {pages.map((p, i) => {
          if (p === "…") {
            return <span key={`gap-${i}`} className="px-2 text-zinc-500 text-sm">…</span>;
          }
          const active = p === page;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={`w-9 h-9 rounded-md border text-sm font-bold transition-colors ${
                active
                  ? "border-amber-400 bg-amber-400 text-zinc-950"
                  : "border-zinc-700 text-zinc-300 hover:border-amber-400 hover:text-amber-300"
              }`}
              aria-current={active ? "page" : undefined}
              data-testid={`tourism-pagination-page-${p}`}
            >
              {p}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-3 h-9 rounded-md border border-zinc-700 text-zinc-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1 hover:border-amber-400 hover:text-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="tourism-pagination-next"
        >
          NEXT<ChevronRight className="h-3.5 w-3.5"/>
        </button>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold" data-testid="tourism-pagination-summary">
        Showing {startIdx}–{endIdx} of {total} cities
      </div>
    </div>
  );
}

